// Core Vue imports
import App from '@/App.vue'
// App services
import type { StateChange } from '@/ably'
// Styles
import '@/assets/main.scss'
import AsyncWrapper from '@/components/AsyncWrapper.vue'
import ElementVisibility from '@/components/ElementVisibility.vue'
// Component imports (could be moved to separate file for better organization)
import AppBanner from '@/components/banners/AppBanner.vue'
import AppCarousel from '@/components/carousel/AppCarousel.vue'
import AppGridCarousel from '@/components/carousel/grid/AppGridCarousel.vue'
import AppAccordion from '@/components/common/AppAccordion.vue'
import AppAccordionSingle from '@/components/common/AppAccordionSingle.vue'
import AppBadge from '@/components/common/AppBadge.vue'
import AppCountdown from '@/components/common/AppCountdown.vue'
import AppFilter from '@/components/common/AppFilter.vue'
import AppList from '@/components/common/AppList.vue'
import AppPageTitle from '@/components/common/AppPageTitle.vue'
import AppPagination from '@/components/common/AppPagination.vue'
import AppPicture from '@/components/common/AppPicture.vue'
import AppSocialIcons from '@/components/common/AppSocialIcons.vue'
import AppTextDisclosure from '@/components/common/AppTextDisclosure.vue'
import AppTrustpilot from '@/components/common/AppTrustpilot.vue'
import SectionGuard from '@/components/common/SectionGuard.vue'
import AppDrawer from '@/components/drawers/AppDrawer.vue'
import AppIcon from '@/components/icons/AppIcon.vue'
import AppButton from '@/components/inputs/AppButton.vue'
import AppCheckbox from '@/components/inputs/AppCheckbox.vue'
import AppForm from '@/components/inputs/AppForm.vue'
import AppInput from '@/components/inputs/AppInput.vue'
import AppMembershipButton from '@/components/inputs/AppMembershipButton.vue'
import AppProgress from '@/components/inputs/AppProgress.vue'
import AppRange from '@/components/inputs/AppRange.vue'
import AppSelect from '@/components/inputs/AppSelect.vue'
import AppSwitch from '@/components/inputs/AppSwitch.vue'
import AppTextButton from '@/components/inputs/AppTextButton.vue'
import AppToggle from '@/components/inputs/AppToggle.vue'
import AppInformationModal from '@/components/modals/AppInformationModal.vue'
import AppModal from '@/components/modals/AppModal.vue'
import AppRegisterSignInModal from '@/components/modals/AppRegisterSignInModal.vue'
import AppUpgradeModal from '@/components/modals/AppUpgradeModal.vue'
import AppYouTubeModal from '@/components/modals/AppYouTubeModal.vue'
import AppSection from '@/components/sections/AppSection.vue'
import router from '@/router'
import { Calendar } from '@/services/calendar'
// Stores
import { useAlertStore } from '@/stores/alert'
import { usePlansStore } from '@/stores/plans'
import { useUserStore } from '@/stores/user'
// Utility imports
import { Icons } from '@/utils/helper-objects'
import {
  toCfAssetUrl,
  toCfResizeUrl,
  toFullUrl,
} from '@/utils/url-generation-utils'
// Third-party libraries
import Echo from '@ably/laravel-echo'
import { cookieBot } from '@ambitiondev/vue-cookiebot'
import { type RumEvent, datadogRum } from '@datadog/browser-rum'
import { IconName, TabSheetType } from '@goodlifeplus/component-library/helpers'
import {
  IconButtonSize,
  IconButtonVariant,
} from '@goodlifeplus/component-library/helpers'
import {
  TextButtonIconPosition,
  TextButtonSize,
  TextButtonVariant,
} from '@goodlifeplus/component-library/helpers'
import { getTheme } from '@goodlifeplus/themes'
import type { SiteSlug } from '@goodlifeplus/themes/types'
import { createGtm } from '@gtm-support/vue-gtm'
import * as Ably from 'ably'
import 'animate.css'
import type { AppEnv } from 'env'
import FloatingVue from 'floating-vue'
import 'floating-vue/dist/style.css'
import { DateTime } from 'luxon'
import { createPinia } from 'pinia'
import 'virtual:theme.css'
import { createApp } from 'vue'
import Toast, { useToast } from 'vue-toastification'
import 'vue-toastification/dist/index.css'

import { i18n } from './i18n'
import { initializeGA } from './plugins/useGtm'
import { RewardToastSystem } from './services/reward-toast-service'
import { SettingsService } from './services/settings'
import { UUIDTracking } from './services/uuid-tracking'
import { useCustomerSafetyStore } from './stores/customer-safety'
import { useFeatureFlagsStore } from './stores/feature-flags'
import { useNotificationStore } from './stores/notification'
import { pluralise } from './utils/utils'

// Environment variables
const APP_ENV = import.meta.env.MODE as AppEnv
const APP_DEBUG = import.meta.env.GLP_APP_ENV !== 'production'

// Define global constants
export const globalConstants = {
  APP_ENV,
  APP_DEBUG,
  BASE_URL: import.meta.env.GLP_BASE_URL,
  PORTAL_URL: import.meta.env.GLP_PORTAL_URL,
  API_URL: import.meta.env.GLP_API_URL,
  GLX_URL: import.meta.env.GLP_GLX_URL,
  BROADCASTING_URL: import.meta.env.GLP_BROADCASTING_URL,
  GLIDE_URL: import.meta.env.GLP_GLIDE_URL,
  CMS_URL: import.meta.env.GLP_CMS_URL,
  CLOUDFLARE_ASSET_URL: import.meta.env.GLP_CLOUDFLARE_ASSET_URL,
  BIG_PRIZE_VALUE: Number(import.meta.env.GLP_BIG_PRIZE_VALUE ?? 9000),

  BUSINESS_NAME: import.meta.env.GLP_BUSINESS_NAME,
  BUSINESS_PHONE: import.meta.env.GLP_BUSINESS_PHONE,
  BUSINESS_EMAIL: import.meta.env.GLP_BUSINESS_EMAIL,
  BUSINESS_ADDRESS: import.meta.env.GLP_BUSINESS_ADDRESS,
  BUSINESS_REGISTERED_NAME: import.meta.env.GLP_BUSINESS_REGISTERED_NAME,
  BUSINESS_COMPLAINTS_EMAIL: import.meta.env.GLP_BUSINESS_COMPLAINTS_EMAIL,
  BUSINESS_WINNERS_EMAIL: import.meta.env.GLP_BUSINESS_WINNERS_EMAIL,
  BUSINESS_INSTANT_WINS_EMAIL: import.meta.env.GLP_BUSINESS_INSTANT_WINS_EMAIL,

  REFERRAL_CREDIT_VALUE: 20,
  REFERRAL_DISCOUNT_VALUE: 0.5,
  SOCIALS_ENTRIES_VALUE: 25,

  EARN_POINTS_SUBSCRIPTION_VALUE: 40,
  EARN_POINTS_YEAR_VALUE: 500,
  EARN_POINTS_SPEND_VALUE: 2,

  $p: pluralise,
  $router: router,
  $i18n: i18n.global,
  $toFullUrl: toFullUrl,
  $toCfResizeUrl: toCfResizeUrl,
  $toCfAssetUrl: toCfAssetUrl,
  $calendar: Calendar,
  $settings: SettingsService,
  $theme: getTheme(import.meta.env.GLP_SITE_SLUG as SiteSlug),
  $toast: useToast(),

  DateTime,
  Icons,

  UI: {
    Icon: {
      Name: IconName,
    },
    IconButton: {
      Variant: IconButtonVariant,
      Size: IconButtonSize,
    },
    TextButton: {
      Variant: TextButtonVariant,
      Size: TextButtonSize,
      IconPosition: TextButtonIconPosition,
    },
    TabSheet: {
      Type: TabSheetType,
    },
  },
}

// Create pinia store
const pinia = createPinia()
pinia.use(() => ({
  ...globalConstants,
}))

// Create Vue application
const app = createApp(App)

// https://github.com/vuejs/core/issues/3031#issuecomment-1085291941
app.provide('globalConstants', globalConstants)

// Initialize UUID tracking (reads from Server-Timing or cookie)
UUIDTracking.initialize()

// Configure plugins
app.use(i18n)
app.use(pinia)
app.use(router)
app.use(FloatingVue, {
  themes: {
    tooltip: {
      triggers: ['hover', 'focus', 'touch', 'click'],
    },
  },
})
app.use(Toast, {
  maxToasts: 3,
  shareAppContext: true,
})
app.use(cookieBot, {
  cookieBotId: import.meta.env.GLP_COOKIEBOT_ID,
})

initializeGA()

app.use(
  createGtm({
    id: 'GTM-TVGHVDB4',
    // queryParams: {
    //   // Add URL query string when loading gtm.js with GTM ID (required when using custom environments)
    //   gtm_auth: 'AB7cDEf3GHIjkl-MnOP8qr',
    //   gtm_preview: 'env-4',
    //   gtm_cookies_win: 'x',
    // },
    // source: 'https://customurl.com/gtm.js', // Add your own serverside GTM script
    defer: false, // Script can be set to `defer` to speed up page load at the cost of less accurate results (in case visitor leaves before script is loaded, which is unlikely but possible). Defaults to false, so the script is loaded `async` by default
    compatibility: false, // Will add `async` and `defer` to the script tag to not block requests for old browsers that do not support `async`
    nonce: '2726c7f26c', // Will add `nonce` to the script tag
    enabled: true, // defaults to true. Plugin can be disabled by setting this to false for Ex: enabled: !!GDPR_Cookie (optional)
    debug: true, // Whether or not display console logs debugs (optional)
    loadScript: true, // Whether or not to load the GTM Script (Helpful if you are including GTM manually, but need the dataLayer functionality in your components) (optional)
    vueRouter: router, // Pass the router instance to automatically sync with router (optional)
    trackOnNextTick: false, // Whether or not call trackView in Vue.nextTick
  })
)

if (
  Number(import.meta.env.GLP_DATADOG_ENABLED) &&
  !window.IS_DATADOG_INITIALISED &&
  window.DD_RUM
) {
  window.IS_DATADOG_INITIALISED = true
  datadogRum.init({
    applicationId: import.meta.env.GLP_DATADOG_APPLICATION_ID,
    clientToken: import.meta.env.GLP_DATADOG_CLIENT_TOKEN,
    site: 'datadoghq.eu',
    service: 'good-life-frontend',
    env: APP_ENV,
    sessionSampleRate: Number(import.meta.env.GLP_DATADOG_SESSION_SAMPLE_RATE),
    sessionReplaySampleRate: Number(
      import.meta.env.GLP_DATADOG_SESSION_REPLAY_SAMPLE_RATE
    ),
    defaultPrivacyLevel: 'mask-user-input',
    beforeSend: (event: RumEvent) => {
      const excludedResources =
        /goodlifeplus(.+)(\/src\/|\/data\/|\/node_modules\/|\/assets\/)/
      if (
        event.type === 'resource' &&
        excludedResources.test(event.resource.url)
      ) {
        return false
      }
      return true
    },
  })
}

// Register global components
// Consider using auto-registration or component libraries in the future
Object.entries({
  AppAccordion,
  AppAccordionSingle,
  AppBadge,
  AppBanner,
  AppButton,
  AppCarousel,
  AppCheckbox,
  AppCountdown,
  AppDrawer,
  AppFilter,
  AppForm,
  AppGridCarousel,
  AppIcon,
  AppInformationModal,
  AppInput,
  AppList,
  AppMembershipButton,
  AppModal,
  AppPageTitle,
  AppPagination,
  AppPicture,
  AppProgress,
  AppRange,
  AppRegisterSignInModal,
  AppSection,
  AppSelect,
  AppSocialIcons,
  AppSwitch,
  AppTextDisclosure,
  AppTextButton,
  AppToggle,
  AppTrustpilot,
  AppUpgradeModal,
  AppYouTubeModal,
  SectionGuard,
  // Renderless components
  AsyncWrapper,
  ElementVisibility,
}).forEach(([name, component]) => {
  app.component(name, component)
})

// Define global properties
app.config.globalProperties = {
  ...app.config.globalProperties,
  ...globalConstants,
  $userStore: useUserStore(),
  $customerSafetyStore: useCustomerSafetyStore(),
  $plansStore: usePlansStore(),
  $alertStore: useAlertStore(),
  $features: useFeatureFlagsStore(),
  $notificationStore: useNotificationStore(),
}

app.config.performance = globalConstants.APP_DEBUG

// Configure Ably broadcasting
window.Ably = Ably
window.Echo = new Echo({
  broadcaster: 'ably',
  authEndpoint: import.meta.env.GLP_BROADCASTING_URL,
})

// Monitor Ably connection state
window.Echo.connector.ably.connection.on((stateChange: StateChange) => {
  if (stateChange.current === 'failed') {
    console.error('Ably connection failed:', stateChange.reason)
  }
  if (stateChange.current === 'connected') {
    console.log('Ably server connected')
  }
})

// Disable console in production
if (import.meta.env.GLP_APP_ENV === 'production') {
  console.log = () => {}
  console.debug = () => {}
  console.info = () => {}
  console.warn = () => {}
}

/**
 * Ensure device ID for guest users.
 *
 * @returns {string} Device ID
 */
function ensureDeviceId(): string {
  let deviceId = localStorage.getItem('device_id')
  if (!deviceId) {
    deviceId =
      'device_' +
      Math.random().toString(36).substring(2, 15) +
      Math.random().toString(36).substring(2, 15)
    localStorage.setItem('device_id', deviceId)
  }

  return deviceId
}

/**
 * Initialize the application and mount it to the DOM
 */
async function initializeApp() {
  if (
    window.location.hostname === 'competitions.casino.org' ||
    window.location.hostname === 'goodlifeplus.com' ||
    window.location.hostname === 'goodlife.myonlinecasinos.co.uk' ||
    window.location.hostname === 'goodlife.lottoland.co.uk' ||
    window.location.hostname === 'saints.goodfanz.com' ||
    window.location.hostname === 'rangers.goodfanz.com' ||
    window.location.hostname === 'saintsprizes.com' ||
    window.location.hostname === 'rangersprizes.com' ||
    /\.pages\.dev$/.test(window.location.hostname)
  ) {
    const meta = document.createElement('meta')
    meta.name = 'robots'
    meta.content = 'noindex'
    document.head.appendChild(meta)
  }

  ensureDeviceId()

  try {
    const featureFlagsStore = useFeatureFlagsStore()
    const cacheLoaded = featureFlagsStore.initializeFromCache()

    if (!cacheLoaded) {
      await featureFlagsStore.fetchFeatureFlags()
    }
  } catch (error) {
    console.error('Failed to initialize feature flags:', error)
  }

  app.mount('#app')

  useToast().updateDefaults({
    container: document.getElementById('main-container')!,
  })
}

RewardToastSystem.initialize({
  toast: useToast(),
  userStore: useUserStore(),
  i18n: globalConstants.$i18n,
})

// Initialize when router is ready
void router.isReady().then(initializeApp)
